import getClusterResolutions from "../../../utils/get-cluster-resolutions";
import getCurrentResolution from "../../../utils/get-current-resolution";
import getFunctionData from "../../../utils/get-function-data";
import { G_multiFunctionParents } from "../../../utils/globals/globals";
import { G_listTransforms } from "../../../utils/globals/list-transforms";
import indexStartWith from "../../../utils/index-start-with";
import getPropertyShorthand from "./get-property-shorthand";

/**
 * Get property wrapper (transform)
 * @description Check if the property should be wrapped in a wrapper (usually a `transform`)
 * @param {String} parent
 * @returns {PropertyWrapperProps}
 */
const getPropertyWrapper = (parent) => {
  const result = new Map();

  const data = getFunctionData(parent);
  const name = getPropertyShorthand(data.get("name")?.toString() || "");
  let parameters = /** @type {String} */ (data.get("parameters"));

  if (parameters?.includes("✦")) {
    const matches = parameters.match(/\[(.*?)\]+/g) || [];

    for (const match of matches) {
      /**
       * Get possible resolution cluster
       * @example `[translateX(2px) xl::translateX(19px)]`
       */
      const resolutions = getClusterResolutions(match);

      /**
       * Get current resolution string name
       * @example `xl`
       */
      const resolution = getCurrentResolution(resolutions);

      /**
       * Get value of current resolution
       * @example `translateX(19px)`
       */
      const current = /** @type {String} */ (resolutions.get(resolution));

      parameters = parameters.replace(match, current);
    }
  }

  const isNoParentTransform = G_listTransforms.some((item) =>
    indexStartWith(name, [item])
  );

  const isMultiParent = G_multiFunctionParents.has(name);
  if (isNoParentTransform) {
    const hasOptions = /\<(.*?)\>/.test(parameters);

    result.set("name", "transform");

    if (parameters.includes("╳")) {
      parameters = parameters.replace(/╳/g, "▏");
    }

    if (hasOptions) {
      const exec = /\<(.*?)\>/.exec(parameters);
      const options = exec?.[0];
      const index = exec?.index;
      const value = parameters.slice(0, index);

      result.set("parameters", `${name}(${value})${options}`);
    } else {
      result.set("parameters", `${name}(${parameters})`);
    }

    result.set("fn", name);
  } else if (isMultiParent) {
    const fn = getFunctionData(parameters);
    const fnName = fn.get("name");

    result.set("name", name);
    result.set("parameters", parameters);
    result.set("fn", fnName);
  } else {
    result.set("name", name);
    result.set("parameters", parameters);
    result.set("fn", null);
  }

  result.set("priority", data.get("priority"));

  return result;
};

export default getPropertyWrapper;
